<template>
    <div class="guest-to-account" v-if="!registrationSuccess">
        <form class="guest-to-account__form" method="post" @submit.prevent="validateAndSubmit" ref="guestToAccountForm">
            <div class="guest-to-account__header">
                <h3 class="guest-to-account__headline">{{ headline }}</h3>
                <p class="guest-to-account__description">{{ description }}</p>
            </div>

            <div class="guest-to-account__fields">
                <div class="input-unit">
                    <input
                        type="email"
                        name="email"
                        :id="'guest-email-' + _uid"
                        :value="guestEmail"
                        readonly
                        disabled
                        class="guest-to-account__email-input"
                    >
                    <label :for="'guest-email-' + _uid">{{ $translate("Ceres::Template.loginEmail") }}</label>
                </div>

                <div class="input-unit" data-validate="password" ref="passwordInput">
                    <popper v-cloak trigger="focus" placement="bottom" ref="passwordHint">
                        <template #handle>
                            <input
                                type="password"
                                name="password"
                                autocomplete="new-password"
                                :id="'guest-password-' + _uid"
                                v-model="password"
                            >
                        </template>

                        <template #title>
                            <div>{{ $translate("Ceres::Template.regPasswordHintTitle") }}</div>
                        </template>

                        <template #content>
                            <ul class="pl-3">
                                <li>{{ $translate("Ceres::Template.regPasswordHintLength") }}</li>
                                <li>{{ $translate("Ceres::Template.regPasswordHintDigit") }}</li>
                                <li>{{ $translate("Ceres::Template.regPasswordHintChar") }}</li>
                            </ul>
                        </template>
                    </popper>
                    <label :for="'guest-password-' + _uid">{{ $translate("Ceres::Template.regPassword") }}*</label>
                </div>

                <div class="input-unit" data-validate="ref">
                    <input
                        type="password"
                        name="password-repeat"
                        autocomplete="new-password"
                        :id="'guest-password-repeat-' + _uid"
                        v-model="passwordRepeat"
                        :data-validate-ref="'#guest-password-' + _uid"
                    >
                    <label :for="'guest-password-repeat-' + _uid">{{ $translate("Ceres::Template.regRepeatPassword") }}*</label>
                </div>

                <input class="honey" type="text" name="username" autocomplete="new-password" tabindex="-1" v-model="honeypot">
            </div>

            <div class="guest-to-account__privacy" v-if="privacyUrl || gtcUrl">
                <small>
                    {{ $translate("Ceres::Template.regPrivacyPolicyHint") }}
                    <template v-if="privacyUrl">
                        <a :href="privacyUrl" target="_blank">{{ $translate("Ceres::Template.contactPrivacyPolicy") }}</a>
                    </template>
                    <template v-if="privacyUrl && gtcUrl"> {{ $translate("Ceres::Template.contactAnd") }} </template>
                    <template v-if="gtcUrl">
                        <a :href="gtcUrl" target="_blank">{{ $translate("Ceres::Template.contactGtc") }}</a>
                    </template>
                </small>
            </div>

            <div class="guest-to-account__actions">
                <button
                    type="submit"
                    :disabled="isDisabled"
                    class="btn btn-primary btn-appearance"
                >
                    {{ $translate("Ceres::Template.regRegister") }}
                    <icon icon="user-plus" class="default-float" :loading="isDisabled"></icon>
                </button>
            </div>
        </form>
    </div>

    <div class="guest-to-account__result" v-else>
        <div class="alert alert-success" v-if="registrationSuccess">
            {{ successMessage }}
        </div>
    </div>
</template>

<script>
import ValidationService from "../services/ValidationService";
import NotificationService from "../services/NotificationService";
import { isNullOrUndefined } from "../helper/utils";

export default {
    name: "guest-to-account",

    props: {
        guestEmail: {
            type: String,
            required: true
        },
        addressId: {
            type: Number,
            required: true
        },
        privacyUrl: {
            type: String,
            default: ""
        },
        gtcUrl: {
            type: String,
            default: ""
        },
        headline: {
            type: String,
            default: "Nächstes Mal schneller einkaufen"
        },
        description: {
            type: String,
            default: "Erstelle dir jetzt ein kostenloses Kundenkonto und speichere deine Adressen."
        },
        successMessage: {
            type: String,
            default: "Dein Kundenkonto wurde erfolgreich erstellt. Du kannst dich jetzt mit deiner E-Mail-Adresse und deinem Passwort einloggen."
        },
        errorEmailExists: {
            type: String,
            default: "Es existiert bereits ein Konto mit dieser E-Mail-Adresse. Bitte logge dich ein oder nutze die Passwort-Zurücksetzen-Funktion."
        },
        errorGeneral: {
            type: String,
            default: "Bei der Erstellung deines Kontos ist ein Fehler aufgetreten. Bitte versuche es später erneut."
        }
    },

    data() {
        return {
            password: "",
            passwordRepeat: "",
            isDisabled: false,
            honeypot: "",
            registrationSuccess: false
        };
    },

    methods: {
        validateAndSubmit() {
            ValidationService.validate(this.$refs.guestToAccountForm)
                .done(() => {
                    this.sendConversion();
                })
                .fail(invalidFields => {
                    if (!isNullOrUndefined(this.$refs.passwordHint) && invalidFields.indexOf(this.$refs.passwordInput) >= 0) {
                        this.$refs.passwordHint.showPopper();
                    }

                    const invalidFieldNames = this.getInvalidFieldNames(invalidFields);

                    if (invalidFieldNames.length > 0) {
                        NotificationService.error(
                            this.$translate("Ceres::Template.checkoutCheckAddressFormFields", { fields: invalidFieldNames.join(", ") })
                        );
                    }

                    ValidationService.markInvalidFields(invalidFields, "error");
                });
        },

        getInvalidFieldNames(invalidFields = []) {
            const fieldNames = [];

            for (const field of invalidFields) {
                let fieldName = field.lastElementChild.innerHTML.trim();
                fieldName = fieldName.slice(-1) === "*" ? fieldName.slice(0, fieldName.length - 1) : fieldName;
                fieldNames.push(fieldName);
            }

            return fieldNames;
        },

        sendConversion() {
            this.isDisabled = true;

            $.ajax({
                type: 'POST',
                url: '/guest/convert/',
                data: {
                    addressId: this.addressId,
                    password: this.password
                },
                success: (response) => {
                    if (response.success) {
                        NotificationService.success(this.successMessage).closeAfter(10000);
                        this.registrationSuccess = true;

                        // Seite nach kurzer Verzögerung neu laden um eingeloggten Zustand anzuzeigen
                        if (response.loggedIn) {
                            setTimeout(() => {
                                window.location.reload();
                            }, 2000);
                        }
                    } else {
                        this.handleError(response);
                    }
                    this.isDisabled = false;
                },
                error: (xhr) => {
                    let response = {};
                    try {
                        response = JSON.parse(xhr.responseText);
                    } catch (e) {
                        response = { error: 'unknown' };
                    }
                    this.handleError(response);
                    this.isDisabled = false;
                }
            });
        },

        handleError(response) {
            if (response.error === 'emailExists') {
                NotificationService.error(this.errorEmailExists).closeAfter(10000);
            } else {
                NotificationService.error(this.errorGeneral).closeAfter(10000);
            }
        }
    }
};
</script>
